// =====================================================================================
// INVRT Radar — web console — account — delivery — vocabulary, query keys and reads
//
// One place for what the Delivery tab's two sections share: the closed event vocabulary
// with its tenant-facing labels (_shared/webhook_events.ts), the friendly names of the
// five target kinds (docs/API-CONTRACTS-C1.md §6), the query keys every mutation
// invalidates, and the four read hooks. Every key includes `orgId`, so an organisation
// switch refetches rather than showing another tenant's endpoints for a frame (the rule
// hooks/useApi.ts states).
//
// Two wire shapes are declared HERE rather than taken from lib/types.c1.ts because the
// server's own source differs from the contract table and the mirror followed the table:
//   - POST /webhooks/:id/test answers `status_code: null` plus an `error` sentence when no
//     HTTP answer arrived at all (routes/visitor.ts, TestSendOutcome);
//   - POST /delivery/targets/:id/test answers `status_code: null` and `error: null` on
//     success (routes/delivery.ts, testTarget's `outcome`).
// The console renders what the server sends, so the types say what the server sends.
// =====================================================================================

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import type {
  DeliveryAttemptsPage,
  DeliveryTargetKind,
  DeliveryTargetList,
  WebhookDeliveriesPage,
  WebhookEndpointList,
  WebhookEventKind,
} from "@/lib/types.c1";

// ---- vocabulary ------------------------------------------------------------------------------

export interface EventKindOption {
  kind: WebhookEventKind;
  label: string;
  description: string;
}

/** The closed vocabulary, in the order the server lists it. */
export const EVENT_KIND_OPTIONS: readonly EventKindOption[] = [
  {
    kind: "visitor.identified",
    label: "Visitor identified",
    description: "A company or person is identified on one of your sites.",
  },
  {
    kind: "contact.revealed",
    label: "Contact revealed",
    description: "A person's contact details are revealed through prospecting.",
  },
];

/** The server's default for a NEW webhook endpoint (DEFAULT_WEBHOOK_EVENTS). */
export const DEFAULT_WEBHOOK_EVENTS: WebhookEventKind[] = ["visitor.identified"];

/** The server's default for a NEW delivery target (DEFAULT_DELIVERY_EVENTS): both kinds. */
export const DEFAULT_TARGET_EVENTS: WebhookEventKind[] = ["visitor.identified", "contact.revealed"];

/** A label for any event string — rows written before the vocabulary closed may carry others. */
export function eventKindLabel(kind: string | null | undefined): string {
  if (!kind) return "—";
  return EVENT_KIND_OPTIONS.find((option) => option.kind === kind)?.label ?? kind;
}

export const TARGET_KINDS: readonly DeliveryTargetKind[] = [
  "email",
  "hubspot",
  "pipedrive",
  "invrt_crm",
  "monti",
];

export const TARGET_KIND_LABELS: Record<DeliveryTargetKind, string> = {
  email: "Email",
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  invrt_crm: "INVRT CRM",
  monti: "MONTI",
};

/** "HTTP 500" or an honest dash when no HTTP answer has been recorded. */
export function httpStatusText(code: number | null | undefined): string {
  return typeof code === "number" ? `HTTP ${code}` : "—";
}

// ---- wire shapes the server actually sends (see the header) ----------------------------------

/** POST /webhooks/:id/test */
export interface WebhookTestOutcome {
  ok: boolean;
  /** Null when no HTTP answer arrived (timeout, unreachable). */
  status_code: number | null;
  duration_ms: number;
  /** The server's own tenant-safe sentence, present only when status_code is null. */
  error?: string;
}

/** POST /delivery/targets/:id/test */
export interface TargetTestOutcome {
  ok: boolean;
  status_code?: number | null;
  error?: string | null;
}

// ---- query keys ------------------------------------------------------------------------------

export const webhooksKey = (orgId: string | null) => ["webhooks", orgId] as const;
export const webhookDeliveriesKey = (orgId: string | null, endpointId: string) =>
  ["webhook-deliveries", orgId, endpointId] as const;
export const targetsKey = (orgId: string | null) => ["delivery-targets", orgId] as const;
export const attemptsKey = (orgId: string | null, targetId: string) =>
  ["delivery-attempts", orgId, targetId] as const;

/** The most rows one drawer asks for; the server's own default page, and plenty to read. */
export const DRAWER_PAGE_LIMIT = 50;

// ---- reads -----------------------------------------------------------------------------------

export function useWebhooks(): UseQueryResult<WebhookEndpointList> {
  const api = useApiClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: webhooksKey(orgId),
    queryFn: () => api.get<WebhookEndpointList>("/webhooks"),
    enabled: Boolean(orgId),
  });
}

export function useDeliveryTargets(): UseQueryResult<DeliveryTargetList> {
  const api = useApiClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: targetsKey(orgId),
    queryFn: () => api.get<DeliveryTargetList>("/delivery/targets"),
    enabled: Boolean(orgId),
  });
}

export function useWebhookDeliveries(endpointId: string): UseQueryResult<WebhookDeliveriesPage> {
  const api = useApiClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: webhookDeliveriesKey(orgId, endpointId),
    queryFn: () =>
      api.get<WebhookDeliveriesPage>(`/webhooks/${endpointId}/deliveries`, {
        query: { limit: DRAWER_PAGE_LIMIT },
      }),
    enabled: Boolean(orgId),
  });
}

export function useDeliveryAttempts(targetId: string): UseQueryResult<DeliveryAttemptsPage> {
  const api = useApiClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: attemptsKey(orgId, targetId),
    queryFn: () =>
      api.get<DeliveryAttemptsPage>(`/delivery/targets/${targetId}/attempts`, {
        query: { limit: DRAWER_PAGE_LIMIT },
      }),
    enabled: Boolean(orgId),
  });
}
